import React, { useEffect, useState } from "react";
import styled from "styled-components";
import SalesChart from "./SalesChart";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { Box, Grid, Stack, TextField, Typography } from "@mui/material";
import SalesCategoryChart from "./SalesCategoryChart";
import SalesSellerChart from "./SalesSellerChart";
import font from "src/styles/font";
import device from "src/styles/device";

import * as adminDashboardApi from "src/api/dashboard/adminPerformanceDashboard";
import moment from "moment";
import { convertIntToCurrency } from "src/common";
import { useSnackbar } from "notistack";

const BoxWrapper = styled.div`
  width: 100%;
  height: auto;
  padding: 32px;
  border-radius: 32px;
  background-color: #ffffff;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.1);
  // display: flex;
  justify-content: center;
  align-items: center;
  /* margin-bottom: 32px;   */

  @media ${device.tabletL} {
    padding: 32px 16px;
  }
`;

function DashboardAdmin() {
  const { enqueueSnackbar } = useSnackbar();

  const today = new Date();
  const last30Days = new Date();
  last30Days.setDate(today.getDate() - 30);

  const [startDate, setStartDate] = useState(last30Days);
  const [endDate, setEndDate] = useState(today);

  const [salesBySeller, setSalesBySeller] = useState([]);
  const [salesByCategory, setSalesByCategory] = useState([]);
  const [totalSalesPerDay, setTotalSalesPerDay] = useState([]);

  const [selectedSeller, setSelectedSeller] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const totalValue = salesByCategory.reduce((acc, item) => acc + item.value, 0);

  const handleSelectSeller = (seller) => {
    if (selectedSeller?._id === seller?._id) {
      setSelectedSeller(null);
      return;
    }
    setSelectedSeller(seller);
    setSelectedCategory(null);
  };

  const handleSelectCategory = (category) => {
    if (selectedCategory?._id === category?._id) {
      setSelectedCategory(null);
      return;
    }
    setSelectedCategory(category);
    setSelectedSeller(null);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const fetchSalesData = async () => {
    try {
      const salesBySellerData = await adminDashboardApi.getSalesBySeller(
        moment(startDate).format("YYYY-MM-DD"),
        moment(endDate).format("YYYY-MM-DD"),
      );
      setSalesBySeller(salesBySellerData);

      const salesByCategoryData = await adminDashboardApi.getSalesByCategory(
        moment(startDate).format("YYYY-MM-DD"),
        moment(endDate).format("YYYY-MM-DD"),
      );
      setSalesByCategory(salesByCategoryData);

      const totalSalesPerDayData = await adminDashboardApi.getTotalSalesPerDay(
        selectedSeller?._id,
        selectedCategory?._id,
        moment(startDate).format("YYYY-MM-DD"),
        moment(endDate).format("YYYY-MM-DD"),
      );
      setTotalSalesPerDay(totalSalesPerDayData);
    } catch (error) {
      console.error("Error fetching sales data:", error);
    }
  };

  useEffect(() => {
    fetchSalesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSeller, selectedCategory, startDate, endDate]);

  useEffect(() => {
    setSelectedCategory(null);
  }, [startDate, endDate]);

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <BoxWrapper>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexDirection: {
                  xs: "column",
                  sm: "row",
                },
                gap: 2,
              }}
            >
              <Stack
                spacing={1}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: {
                    xs: "center",
                    sm: "flex-start",
                  },
                }}
              >
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{
                    fontSize: {
                      xs: `${font.size.lg} !important`,
                      sm: `${font.size.xl} !important`,
                    },
                  }}
                >
                  ภาพรวมยอดขาย
                </Typography>
                <Typography
                  sx={{
                    color: "#f44336",
                    fontWeight: 700,
                    fontSize: {
                      xs: `2rem !important`,
                    },
                    whiteSpace: "nowrap",
                  }}
                  variant="h4"
                >
                  ฿ {convertIntToCurrency(totalValue)}
                </Typography>
              </Stack>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <DatePicker
                    value={startDate}
                    inputFormat="DD/MM/YYYY"
                    onChange={(newValue) => {
                      // Check date difference not more than 1 year
                      const _startDate = dayjs(newValue);
                      const _endDate = dayjs(endDate);
                      const diff = _endDate.diff(_startDate, "day");
                      if (diff > 365) {
                        enqueueSnackbar(
                          "ไม่สามารถเลือกช่วงเวลามากกว่า 1 ปีได้",
                          {
                            variant: "error",
                          },
                        );
                        return;
                      }
                      const formattedDate = dayjs(newValue).isValid()
                        ? dayjs(newValue).format("YYYY-MM-DD")
                        : null;
                      setStartDate(formattedDate);
                    }}
                    maxDate={dayjs(new Date(endDate))}
                    renderInput={(params) => (
                      <TextField
                        onKeyDown={onKeyDown}
                        {...params}
                        inputProps={{ ...params.inputProps, readOnly: true }}
                        sx={{
                          mx: 1,
                          width: "100%",
                          maxWidth: 200,
                          background: "#fff",
                          ".MuiOutlinedInput-root": {
                            paddingRight: "18px",
                          },
                        }}
                        label="ตั้งแต่"
                        size="small"
                      />
                    )}
                  />
                  -
                  <DatePicker
                    value={endDate}
                    inputFormat="DD/MM/YYYY"
                    onChange={(newValue) => {
                      const _startDate = dayjs(startDate);
                      const _endDate = dayjs(newValue);
                      const diff = _endDate.diff(_startDate, "day");
                      if (diff > 365) {
                        enqueueSnackbar(
                          "ไม่สามารถเลือกช่วงเวลามากกว่า 1 ปีได้",
                          {
                            variant: "error",
                          },
                        );
                        return;
                      }
                      const formattedDate = dayjs(newValue).isValid()
                        ? dayjs(newValue).format("YYYY-MM-DD")
                        : null;
                      setEndDate(formattedDate);
                    }}
                    minDate={dayjs(new Date(startDate))}
                    maxDate={dayjs(new Date())}
                    renderInput={(params) => (
                      <TextField
                        onKeyDown={onKeyDown}
                        {...params}
                        inputProps={{ ...params.inputProps, readOnly: true }}
                        sx={{
                          ml: 1,
                          width: "100%",
                          maxWidth: 200,
                          background: "#fff",
                          ".MuiOutlinedInput-root": {
                            paddingRight: "18px",
                          },
                        }}
                        label="ถึง"
                        size="small"
                      />
                    )}
                  />
                </Box>
              </LocalizationProvider>
            </Box>
          </BoxWrapper>
        </Grid>
        <Grid item xs={12} lg={6}>
          <BoxWrapper style={{ height: "100%" }}>
            <SalesSellerChart
              data={salesBySeller}
              selectedSeller={selectedSeller}
              onSelect={handleSelectSeller}
            />
          </BoxWrapper>
        </Grid>
        <Grid item xs={12} lg={6}>
          <BoxWrapper style={{ height: "100%" }}>
            <SalesCategoryChart
              data={salesByCategory}
              selectedCategory={selectedCategory}
              onSelect={handleSelectCategory}
            />
          </BoxWrapper>
        </Grid>
        <Grid item xs={12}>
          <BoxWrapper>
            <SalesChart
              data={totalSalesPerDay}
              selectedSeller={selectedSeller}
              selectedCategory={selectedCategory}
            />
          </BoxWrapper>
        </Grid>
      </Grid>
    </>
  );
}

export default DashboardAdmin;
